import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Home, Play, Upload as UploadIcon, Coins, User, Search, Shield, Bell, LogOut, Crown, ListMusic, Menu, X, Rocket, Sparkles, Download, Tv, Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Wordmark from "@/components/Wordmark";
import { PushPrompt } from "@/components/PushPrompt";
import { PushToggle } from "@/components/PushToggle";
import { InstallBanner, useInstallable, promptInstall } from "@/components/InstallBanner";
import { SiteFooter } from "@/components/SiteFooter";

const tabs = [
  { to: "/", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/shorts", label: "Loop", icon: Play, testid: "nav-shorts" },
  { to: "/upload", label: "Upload", icon: UploadIcon, testid: "nav-upload", featured: true },
  { to: "/coins", label: "Coins", icon: Coins, testid: "nav-coins" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [notifCount, setNotifCount] = useState(0);
  const [drawer, setDrawer] = useState(false);
  const isShorts = loc.pathname.startsWith("/shorts");
  const installable = useInstallable();

  useEffect(() => {
    if (!user) return;
    api.get("/me/notifications").then(({data}) => setNotifCount(data.filter(n => !n.read).length)).catch(()=>{});
  }, [user, loc.pathname]);

  const onSearch = (e) => { e.preventDefault(); if(q.trim()) nav(`/search?q=${encodeURIComponent(q)}`); };
  const first = user?.username?.[0]?.toUpperCase() || "?";

  return (
    <div className="min-h-[100dvh] bg-[#050510] text-white overflow-x-hidden">
      {!isShorts && (
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 h-16 flex items-center gap-2 sm:gap-3">
          <Link to="/" data-testid="brand-logo" className="shrink-0"><Wordmark size="sm"/></Link>
          <form onSubmit={onSearch} className="flex-1 max-w-xl">
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00E7FF]"/>
              <input data-testid="search-input" value={q} onChange={e=>setQ(e.target.value)}
                placeholder="Search Jixxeo Videos…"
                className="w-full h-10 rounded-full bg-black/40 border-2 border-[#00E7FF]/40 pl-10 pr-4 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#00E7FF]"/>
            </div>
          </form>
          {user && (
            <Link to="/notifications" className="relative w-10 h-10 rounded-full border-2 border-[#00E7FF]/60 flex items-center justify-center hover:bg-white/5" data-testid="nav-notifications">
              <Bell size={18} className="text-[#00E7FF]"/>
              {notifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#FF3EA5] text-[10px] font-extrabold flex items-center justify-center border-2 border-[#050510]">{notifCount}</span>
              )}
            </Link>
          )}
          <Link to={user ? "/profile" : "/login"} data-testid="nav-profile-avatar"
                className="w-10 h-10 rounded-full border-2 border-[#00E7FF]/60 overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#00E7FF]/20 to-[#FF3EA5]/20 shrink-0">
            {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover"/> : <User size={16} className="text-[#00E7FF]"/>}
          </Link>
          <button onClick={()=>setDrawer(true)} data-testid="nav-menu" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/5 text-[#00E7FF]">
            <Menu size={22}/>
          </button>
        </div>
      </header>)}

      <main className={isShorts ? "" : "pb-28 lg:pb-8"}>{children}{!isShorts && <SiteFooter/>}</main>
      <PushPrompt/>
      <InstallBanner/>

      {/* Menu drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur" onClick={()=>setDrawer(false)}>
          <div onClick={e=>e.stopPropagation()} className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-[#0a0a18] border-l border-white/10 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <Wordmark size="sm"/>
              <button onClick={()=>setDrawer(false)} className="p-2 rounded-full hover:bg-white/5"><X size={20}/></button>
            </div>
            <div className="space-y-1">
              <DrawerLink to="/" icon={Home} label="Home" onClick={()=>setDrawer(false)} testid="drawer-home"/>
              <DrawerLink to="/shorts" icon={Play} label="Loop" onClick={()=>setDrawer(false)} testid="drawer-loop"/>
              <DrawerLink to="/coins" icon={Coins} label="Coin Shop" onClick={()=>setDrawer(false)} testid="drawer-coins" accent="#FFD700"/>
              <DrawerLink to="/membership" icon={Crown} label="Membership" onClick={()=>setDrawer(false)} testid="drawer-membership"/>
              <DrawerLink to="/playlists" icon={ListMusic} label="Playlists" onClick={()=>setDrawer(false)} testid="drawer-playlists"/>
              <DrawerLink to="/downloads" icon={Download} label="Downloads" onClick={()=>setDrawer(false)} testid="drawer-downloads"/>
              <DrawerLink to="/tv" icon={Tv} label="TV Mode" onClick={()=>setDrawer(false)} testid="drawer-tv"/>
              {installable.available && (
                <button onClick={()=>{setDrawer(false); promptInstall();}} data-testid="drawer-install"
                        className="w-full flex items-center gap-3 px-3 h-11 rounded-xl hover:bg-white/5 jx-btn">
                  <Smartphone size={18} className="text-[#FF3EA5]"/><span className="text-sm font-semibold">Install app</span>
                </button>
              )}
              <DrawerLink to="/upload" icon={UploadIcon} label="Upload" onClick={()=>setDrawer(false)} testid="drawer-upload"/>
              <DrawerLink to="/profile" icon={User} label="Profile" onClick={()=>setDrawer(false)} testid="drawer-profile"/>
              {user?.role === "admin" && <DrawerLink to="/admin" icon={Shield} label="Admin Dashboard" onClick={()=>setDrawer(false)} testid="drawer-admin" accent="#FF3EA5"/>}
            </div>
            <div className="mt-6 pt-6 border-t border-white/10">
              {user ? (
                <div>
                  <div className="text-sm text-white/60">Signed in as</div>
                  <div className="font-bold">@{user.username}</div>
                  <PushToggle testid="drawer-push-toggle" className="mt-4 w-full justify-center"/>
                  <button onClick={()=>{logout(); setDrawer(false); nav('/');}} data-testid="drawer-logout"
                          className="jx-btn mt-4 w-full h-10 rounded-full border border-white/15 hover:bg-white/5 text-sm font-semibold inline-flex items-center justify-center gap-2"><LogOut size={14}/> Sign out</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" onClick={()=>setDrawer(false)} data-testid="drawer-login" className="flex-1 jx-btn h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-sm font-semibold">Sign in</Link>
                  <Link to="/register" onClick={()=>setDrawer(false)} data-testid="drawer-register" className="flex-1 jx-btn h-10 rounded-full bg-[#00E7FF] text-black flex items-center justify-center text-sm font-bold">Join</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10 lg:hidden" data-testid="bottom-nav">
        <div className="grid grid-cols-5 items-end h-20 relative">
          {tabs.map(t => {
            if (t.featured) {
              return (
                <NavLink key={t.to} to={t.to} data-testid={t.testid}
                  className="flex flex-col items-center justify-end gap-1 pb-2 -mt-6">
                  <span className="w-14 h-14 rounded-full bg-[#00E7FF] shadow-[0_0_30px_rgba(0,231,255,0.55)] flex items-center justify-center">
                    <t.icon size={26} className="text-black"/>
                  </span>
                  <span className="text-[10px] font-semibold text-white/80">{t.label}</span>
                </NavLink>
              );
            }
            return (
              <NavLink key={t.to} to={t.to} data-testid={t.testid}
                className={({isActive}) => `flex flex-col items-center justify-center pt-2 pb-3 gap-1 jx-btn relative ${isActive ? 'text-[#00E7FF]' : 'text-white/60'}`}>
                <t.icon size={22}/>
                <span className="text-[10px] font-medium">{t.label}</span>
                {t.to === "/coins" && notifCount > 0 && (
                  <span className="absolute top-2 right-1/2 translate-x-4 w-2 h-2 rounded-full bg-[#FF3EA5]"/>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function DrawerLink({ to, icon: Icon, label, onClick, testid, accent }) {
  return (
    <Link to={to} onClick={onClick} data-testid={testid}
          className="flex items-center gap-3 px-3 h-11 rounded-xl hover:bg-white/5 jx-btn">
      <Icon size={18} style={accent ? { color: accent } : {}} className="text-[#00E7FF]"/>
      <span className="text-sm font-semibold">{label}</span>
    </Link>
  );
}
